import { Link } from 'react-router-dom';
import { ArrowLeft, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Security() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-bold">Luma</span>
          </Link>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-safe">
        <article className="prose prose-invert max-w-none">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Security</h1>
          <p className="text-muted-foreground mb-8">Last Updated: February 2026</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Our Commitment to Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              At Luma, protecting your data is our top priority. We implement industry-leading security 
              measures to ensure your personal information and productivity data remain safe and private.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Data Encryption</h2>
            
            <h3 className="text-lg font-medium mb-2">In Transit</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              All data transmitted between your device and our servers is encrypted using TLS 1.3, 
              the latest and most secure transport layer security protocol. This ensures that your 
              tasks, focus sessions, and personal information cannot be intercepted during transmission.
            </p>

            <h3 className="text-lg font-medium mb-2">At Rest</h3>
            <p className="text-muted-foreground leading-relaxed">
              Your data is stored in encrypted databases using AES-256 encryption, the same standard 
              used by banks and government agencies. Even in the unlikely event of a data breach, 
              your information would remain unreadable.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Authentication & Access Control</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>
                <strong className="text-foreground">Secure Password Storage:</strong> Passwords are 
                hashed using bcrypt with salt rounds, making them impossible to reverse-engineer.
              </li>
              <li>
                <strong className="text-foreground">OAuth Integration:</strong> Sign in securely with 
                Google or Apple using industry-standard OAuth 2.0 protocols.
              </li>
              <li>
                <strong className="text-foreground">Row-Level Security:</strong> Our database implements 
                strict row-level security (RLS) policies, ensuring users can only access their own data.
              </li>
              <li>
                <strong className="text-foreground">Session Management:</strong> Sessions are automatically 
                refreshed and can be revoked at any time through the app settings.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Infrastructure Security</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>
                <strong className="text-foreground">Cloud Hosting:</strong> We use enterprise-grade 
                cloud infrastructure with automatic failover and redundancy.
              </li>
              <li>
                <strong className="text-foreground">Regular Backups:</strong> Automated encrypted backups 
                are performed daily and stored in geographically distributed locations.
              </li>
              <li>
                <strong className="text-foreground">DDoS Protection:</strong> Advanced protection against 
                distributed denial-of-service attacks keeps our services available.
              </li>
              <li>
                <strong className="text-foreground">Monitoring:</strong> 24/7 security monitoring and 
                automated threat detection systems.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Compliance & Certifications</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We adhere to international data protection standards and regulations:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li><strong className="text-foreground">GDPR:</strong> Full compliance with the European General Data Protection Regulation</li>
              <li><strong className="text-foreground">CCPA:</strong> Compliance with the California Consumer Privacy Act</li>
              <li><strong className="text-foreground">SOC 2 Type II:</strong> Our infrastructure provider maintains SOC 2 certification</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Account Deletion</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When you delete your account through the app:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Your authentication credentials are immediately removed, preventing any future login.</li>
              <li>Personal information (email, display name, avatar) is permanently deleted.</li>
              <li>Tasks, notes, and focus session data are anonymized to preserve aggregate statistics while removing all personal identifiers.</li>
              <li>Legally required financial records (transaction IDs, amounts) are retained separately for tax compliance but are not linked to your identity.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Security Best Practices for Users</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Use a strong, unique password for your Luma account</li>
              <li>Enable biometric authentication on your device</li>
              <li>Keep your device's operating system updated</li>
              <li>Log out from shared or public devices</li>
              <li>Report suspicious activity immediately</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Reporting Security Issues</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you discover a security vulnerability, please report it responsibly to:{' '}
              <a href="mailto:security@luma-todo.xyz" className="text-primary hover:underline">
                security@luma-todo.xyz
              </a>
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              We take all reports seriously and will respond within 48 hours.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              For security-related questions or concerns:{' '}
              <a href="mailto:security@luma-todo.xyz" className="text-primary hover:underline">
                security@luma-todo.xyz
              </a>
            </p>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Luma. Focus on what matters.
          </p>
        </div>
      </footer>
    </div>
  );
}
